import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { fetchProfile, fetchWorkspaces } from '@/services/authApi';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading, signOut } = useAuth();
  const location = useLocation();
  const { workspaces, setWorkspaces, setCurrentWorkspace, setUserProfile } =
    useWorkspaceStore();
  const [wsChecked, setWsChecked] = useState(false);
  // Only true when a successful fetch confirmed the user has zero workspaces.
  // Using local state (not Zustand) means reset() clearing the store won't
  // accidentally trigger the /onboarding redirect during sign-out.
  const [hasNoWorkspaces, setHasNoWorkspaces] = useState(false);

  // Fetch workspaces and profile after auth is confirmed (or when auth is disabled)
  useEffect(() => {
    if ((!DISABLE_AUTH && !user) || wsChecked) return;

    let cancelled = false;
    (async () => {
      try {
        const [profile, ws] = await Promise.all([fetchProfile(), fetchWorkspaces()]);
        if (cancelled) return;
        setUserProfile(profile);
        setWorkspaces(ws);
        if (ws.length > 0) {
          // Reconcile the persisted selection against the freshly fetched list
          // so the chosen workspace is actually remembered. Read the current
          // selection fresh from the store (not the effect closure) so a fast
          // user switch during the fetch isn't clobbered. Match by id, then
          // fall back to website (ids can differ across envs/DBs):
          //   - found   → re-point to the FRESH object so its data (name /
          //               website / is_active) and the tenant bridge stay in
          //               sync with the server on every load.
          //   - missing → the persisted workspace is stale (membership change,
          //               different env, list churn) — fall back to the user's
          //               last-active workspace (#1262), then the first
          //               website-backed workspace (so the tenant bridge fires),
          //               instead of defaulting to ws[0] (first alphabetically)
          //               which feeds a wrong tenant_id for multi-workspace users.
          const selected = useWorkspaceStore.getState().currentWorkspace;
          const matched =
            ws.find((w) => w.id === selected?.id) ??
            ws.find((w) => w.website && w.website === selected?.website);
          const lastActive = profile.last_active_workspace_id
            ? ws.find((w) => w.id === profile.last_active_workspace_id)
            : undefined;
          setCurrentWorkspace(matched ?? lastActive ?? ws.find((w) => w.website) ?? ws[0]);
        }
        setHasNoWorkspaces(ws.length === 0);
      } catch (err: any) {
        if (cancelled) return;
        if (DISABLE_AUTH) {
          // Auth disabled — fetch failed but don't sign out, just log it
          console.warn('[ProtectedRoute] Failed to fetch workspaces (auth disabled):', err);
        } else {
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            // Expired session or deactivated account — sign out and let the
            // !user guard below redirect to /login automatically.
            await signOut();
            return;
          }
          console.error('[ProtectedRoute] Failed to fetch workspaces:', err);
        }
      } finally {
        if (!cancelled) setWsChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (DISABLE_AUTH) return <>{children}</>;

  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-coral border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Wait for workspace check to complete before rendering anything
  if (!wsChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-coral border-t-transparent" />
      </div>
    );
  }

  // Redirect a first-run (zero-workspace) user to /onboarding only when BOTH:
  //   1. A completed fetch confirmed no workspaces (hasNoWorkspaces local state), AND
  //   2. The Zustand store also has no workspaces (workspaces.length === 0).
  //
  // /create-workspace remains available for adding another business once the
  // user already has one. A zero-workspace visit there is still first-run and
  // must enter the onboarding package gate.
  //
  // The double-check prevents a spurious redirect when React reuses this
  // ProtectedRoute instance across route changes. Onboarding updates the
  // workspace store before navigating away, so the stale local zero-workspace
  // flag cannot bounce the newly created business back into first-run.
  //
  // Using hasNoWorkspaces (not workspaces.length alone) means store.reset()
  // during sign-out doesn't trigger this redirect — user=null fires first.
  const isOnboardingPath =
    location.pathname === '/onboarding' || location.pathname.startsWith('/onboarding/');

  if (hasNoWorkspaces && workspaces.length === 0 && !isOnboardingPath) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
